import DataQuery from 'data/DataQuery';
import { useQuery } from 'data/hooks/useQuery';
import PageCard from 'features/shared/components/card/PageCard';
import { useForm } from 'features/shared/forms/hooks/useForm';
import React, { useEffect, useState } from 'react';
import BasicInfoSubFormSection from '../basicInfoSubForm/BasicInfoSubFormSection';
import ContractDropdownDTO from 'features/contracts/dtos/ContractDropdownDTO';
import { useSelector } from 'react-redux';
import IAppState from 'infrastructure/store/IAppState';
import CustomerState from 'infrastructure/store/customer/CustomerState';
import { Requirements_SelfReadingContracts } from 'data/endpoints';
import { getQueryParams } from 'features/shared/helpers/queryParams';
import SelfReadingTypeEnumDTO from 'features/requirements/dtos/SelfReadingTypeEnumDTO';
import { useInjection } from 'infrastructure/ioc/ioc.react';
import { IocTypes } from 'infrastructure/ioc/ioc.types';
import SelfReadingMeterStates from 'features/requirements/components/selfReadingForm/SelfReadingMeterStates';
import SelfReadingDate from 'features/requirements/components/selfReadingForm/SelfReadingDate';
import SelfReadingPresetDateRolesDTO from 'features/requirements/dtos/SelfReadingPresetDateRolesDTO';
import SelfReadingMeter from 'features/requirements/components/selfReadingForm/SelfReadingMeter';
import SelfReadingType from 'features/requirements/components/selfReadingForm/SelfReadingType';
import IRequirementCreateService from 'features/requirements/services/IRequirementCreateService';
import IContentDialogService from 'features/shared/components/contentDialog/IContentDialogService';
import ISelfReadingFormService from 'features/requirements/services/ISelfReadingFormService';
import SelfReadingDatesResult from 'features/requirements/models/SelfReadingDatesResult';
import SelfReadingTypeErrorSubDialog from 'features/requirements/components/selfReadingForm/SelfReadingTypeErrorSubDialog';
import { selfReadingFormValidations, setTypeValidation, setDependentInputsValidation } from './selfReadingFormValidation';
import { useLocation } from 'react-router-dom';
import PromiseLoadingSpinner from 'features/shared/components/loaders/PromiseLoadingSpinner';
import { SelfReadingFormSection_SelfReading } from 'features/shared/components/loaders/promiseLoadingAreas';
import { trackPromise } from 'react-promise-tracker';
import moment from 'moment';
import SelfReadingValidationSubDialog from 'features/requirements/components/selfReadingForm/SelfReadingValidationSubDialog';
import ContractSelect from 'features/contracts/components/ContractSelect';
import BaseRequirementFormProps from 'features/requirements/models/BaseRequirementFormProps';
import { getContractDetailRootRoute } from 'features/shared/helpers/routeHelper';
import PostResponse from 'data/PostResponse';
import { RequirementResultDialogParams } from 'features/requirements/models/RequirementResultDialogParams';
import { openRequirementResultDialog } from 'features/requirements/helpers/requirementResultDialogHelper';
import InsertRequirementResultDTO from 'features/requirements/dtos/InsertRequirementResultDTO';
import { useTranslation } from 'react-i18next';
import FormGroup from 'features/shared/forms/components/FormGroup';

interface Props extends BaseRequirementFormProps {
  selectedContractId?: string;
}

const SelfReadingFormSection = (props?: Props) => {
  // translations
  const tLabels = useTranslation("requirements\\requirements").t;

  // DI
  const createService = useInjection<IRequirementCreateService>(IocTypes.RequirementCreateService);
  const dialogService = useInjection<IContentDialogService>(IocTypes.ContentDialogService);
  const formService = useInjection<ISelfReadingFormService>(IocTypes.SelfReadingFormService);

  // router location
  const { state: selectedContractIdLocation  } = useLocation<string>();

  // props
  const useOutsideOfRequirements = props?.useOutsideOfRequirements ? true : false;
  const sendButtonTitle = props?.sendButtonTitle ? props.sendButtonTitle : tLabels("selfReadingFormSection.sendRequirement");
  const selectedContractId = props?.selectedContractId ? props.selectedContractId : selectedContractIdLocation;

  // store
  const customer = (useSelector<IAppState, CustomerState>((store) => store.customer)).customer;

  // state
  const [types, setTypes] = useState<SelfReadingTypeEnumDTO[]>([]);
  const [dates, setDates] = useState<SelfReadingPresetDateRolesDTO>({});
  const [meter, setMeter] = useState<string>("");
  const [selectedContract, setSelectedContract] = useState<ContractDropdownDTO>({});

  // fetch contracts
  let query: DataQuery = {url: Requirements_SelfReadingContracts, params: getQueryParams("CustomerId", customer.customerId!)};
  const { data: contracts } = useQuery<ContractDropdownDTO[]>(query, [], [customer]);

  // form hook
  const { values, errors, bindField, bindUncontrolledField, valideteForm, validateFormField, setFormValue, setValidationRule } = useForm({
    initialValidations: selfReadingFormValidations()
  });

  // side effects
  useEffect(() => {
    setDefaultContract();
  }, [selectedContractId, contracts])

  // handlers
  const submitFormHandler = async (e: React.FormEvent) => {
    e.preventDefault();
    if (valideteForm()) {
      let validationResult = await trackPromise(formService.validateSelfReading(values));

      // valid self-reading => create
      if (validationResult.status < 300 && validationResult.data?.isValid === true) {
        createRequirementHandler();
      }
      // invalid self-reading => show warnings/errors
      else if (validationResult.status < 300 && validationResult.data?.isValid === false) {
        dialogService.openDialog(tLabels("selfReadingFormSection.warning"), () => (
          <SelfReadingValidationSubDialog
            validationResult={validationResult.data!}
            createRequirementHandler={createRequirementHandler}
          />
        ));
      } 
      // error in validation => show error
      else {
        openResultDialogHandler({ status: validationResult.status });
      }
    }
  };

  const createRequirementHandler = async () => {
    let result = await createService.createRequirement("ZADANISAMOODECTU", values);
    openResultDialogHandler(result);
  }

  const openResultDialogHandler = (result: PostResponse<InsertRequirementResultDTO>) => {
    const params: RequirementResultDialogParams = {
      response: result,
      successMessage: tLabels("selfReadingFormSection.selfReadingSuccessMessage"),
      errorMessage: tLabels("selfReadingFormSection.selfReadingErrorMessage"),
      actionBtn: { title: tLabels("selfReadingFormSection.selfReadingDetail"), route: getContractDetailRootRoute(values["DESC_TMSmOdecSmlouva"], `self-readings/${result.data?.createdEntityId}`) },
      transitonBtn: null
    };

    openRequirementResultDialog(useOutsideOfRequirements, params, tLabels("selfReadingFormSection.createSelfReading"));
  }

  const contractChangeHandler = async (contract: ContractDropdownDTO | null) => {
    if (contract && contract.contractId) {
      clearDependentFormValues();
      
      // load types for selected contract
      let result = await trackPromise(formService.fetchTypes(contract.contractId), SelfReadingFormSection_SelfReading);
      
      // set state
      setTypes(result); 
      setSelectedContract(contract);

      // set select data to form values
      setFormValue("DESC_TMSmOdecSmlouva", contract.contractId);
      setFormValue("DESC_TMSmOdecEanEic", contract.placeOfDeliveryIdentification);

      // validate selected contract
      validateFormField("DESC_TMSmOdecSmlouva", contract.contractId);
      
      // set validation rule for type input 
      setTypeValidation(setValidationRule);
    }
  }

  const typeChangeHandler = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    let typeId = e.target.value;

    if (validateFormField("DESC_TMSmOdecDuvod", typeId)) {
      // fetch dates
      let selectedType = types.find(x => x.selfReadingTypeId === typeId);
      let datesResult = await trackPromise(formService.fetchDates(values["DESC_TMSmOdecSmlouva"], selectedType?.code!), SelfReadingFormSection_SelfReading);
      
      const typeChangeValidHandler = async () => {
        // fetch meter
        let meter = await trackPromise(formService.fetchMeter(values["DESC_TMSmOdecSmlouva"], datesResult.dates!.presetDate!), SelfReadingFormSection_SelfReading);

        // set state
        setDates(datesResult.dates!);
        setMeter(meter);

        // set fetch data to form values
        setFormValue("DESC_TMSmOdecDuvod", typeId);
        setFormValue("DESC_TMSmOdecDuvodKod", selectedType?.code)
        setFormValue("DESC_TMSmOdecDatum", datesResult.dates!.presetDate);
        setFormValue("DESC_TMSmOdecMeridlo", String(meter));

        // set validation rules for hidden (dependent) inputs
        setDependentInputsValidation(setValidationRule, selectedContract, types.find(x => x.selfReadingTypeId === typeId));
      };

      if (datesResult.isValid) {
        typeChangeValidHandler();
      } else {
        datesErrorHandler(datesResult, typeChangeValidHandler);
      }   
    }
  }

  const dateChangeHandler = async (date: moment.Moment) => {
    if (validateFormField("DESC_TMSmOdecDatum", String(date))) {
      // fetch meter
      let meter = await trackPromise(formService.fetchMeter(values["DESC_TMSmOdecSmlouva"], date.format()), SelfReadingFormSection_SelfReading);
      
      // set state
      setFormValue("DESC_TMSmOdecDatum", date.format(moment.HTML5_FMT.DATE));
      setMeter(meter || "");
    }
  }
  
  const datesErrorHandler = (datesResult: SelfReadingDatesResult, onCloseDialog: () => Promise<void>) => {
    const { dates } = datesResult;
    
    if (dates && dates.error) {
      clearDependentFormValues();
    }

    dialogService.openDialog(tLabels("selfReadingFormSection.warning"), () => (
      <SelfReadingTypeErrorSubDialog
        datesResult={datesResult}
        onCloseDialog={dates && dates.error === null && dates.warning ? onCloseDialog : undefined}
      />
    ));
  }

  // helpers
  const isFilledContract = () => {
    if (values["DESC_TMSmOdecSmlouva"]) {
      return true;
    }
    return false;
  }
  
  const isFilledContractAndType = (): boolean => {
    if (values["DESC_TMSmOdecSmlouva"] && values["DESC_TMSmOdecDuvod"]) {
      return true;
    }
    return false;
  }

  const clearDependentFormValues = () => {
    clearFormValues(
      "DESC_TMSmOdecDuvod",
      "DESC_TMSmOdecDuvodKod",
      "DESC_TMSmOdecDatum",
      "DESC_TMSmOdecMeridlo",
      "DESC_TMSmOdecStavNT",
      "DESC_TMSmOdecStavVT",
      "DESC_TMSmOdecStav"
    );
  }

  const clearFormValues = (...formKeys: string[]) => {
    for (const formKey of formKeys) {
      setFormValue(formKey, null, "remove");
    }
  }

  const setDefaultContract = () => {
    if (contracts.length > 0 && selectedContractId) {
      const contract = contracts.find(x => x.contractId === selectedContractId);

      if (contract) {
        contractChangeHandler(contract);
      } 
    }
  }

  // sub-renders
  const renderBasicInfoSection = () => {
    if (!useOutsideOfRequirements) {
      return (
        <BasicInfoSubFormSection
          bindField={bindField}
          bindUncontrolledField={bindUncontrolledField}
          errors={errors}
          setValidationRule={setValidationRule}
        />
      );
    }    
  }

  // render
  return (
    <form onSubmit={submitFormHandler}>
      <PageCard title={tLabels("selfReadingFormSection.cardTitle")} bodyClassName="pt-4 px-4">
        <div className="row no-gutters mx-n2 position-relative">
          <div className="col-lg-6 px-2">
            <ContractSelect 
              name="DESC_TMSmOdecSmlouva"
              contracts={contracts}
              onChange={contractChangeHandler}
              errors={errors}
              defaultContract={selectedContract}
            />
          </div>
          <div className="col-sm-6 px-2">
            <FormGroup
              labelText={tLabels("selfReadingFormSection.eanEic")}
              visible={isFilledContract()}
            >
              <input
                className="form-control"
                disabled
                defaultValue={values["DESC_TMSmOdecEanEic"] ?? ""}
              />
            </FormGroup>
          </div>
          <div className="col-sm-6 px-2">
            <SelfReadingType 
              name="DESC_TMSmOdecDuvod"
              types={types}
              value={values["DESC_TMSmOdecDuvod"]}
              onChange={typeChangeHandler}
              editable={isFilledContract()}
              errors={errors}
            />
          </div>
          <div className="col-sm-6 px-2">
            <SelfReadingDate 
              name="DESC_TMSmOdecDatum"
              dates={dates}
              onChange={dateChangeHandler}
              editable={isFilledContractAndType()}
              errors={errors}
            />
          </div>
          <div className="col-sm-6 px-2">
            <SelfReadingMeter 
              name="DESC_TMSmOdecMeridlo"
              bindField={bindField}
              editable={isFilledContractAndType()}
              errors={errors}
            />
          </div>
          <SelfReadingMeterStates
            bindField={bindField}
            errors={errors}
            selfReadingContract={selectedContract}
            editableInputs={isFilledContractAndType()}
          />
          <PromiseLoadingSpinner area={SelfReadingFormSection_SelfReading} delay={100} />
        </div> 
      </PageCard>

      {renderBasicInfoSection()}

      <button type="submit" className="btn btn-primary px-3 py-2">{sendButtonTitle}</button>
    </form>
  )
}

export default SelfReadingFormSection;
